import { splitPostText } from './23-post-continuations.mjs';
// Trusted course content has one versioned owner. Learning progress is explicit reading,
// never mastery, examination readiness or psychological inference.
import { COURSE_POST_TEXT_LIMIT } from './23-post-continuations.mjs';
const ID = /^[a-z0-9][a-z0-9.-]{1,100}$/;
const tiers = [
  'both',
  'both_with_higher_extension',
  'both_different_assessment_demand',
  'higher_only',
  'foundation_only',
  'not_applicable',
];
const biology = ['not_marked', 'whole_leaf', 'partial_extension', 'not_applicable'];
const asScopes = [
  'not_applicable',
  'as_and_a_level',
  'a_level_only',
  'as_reference_list_not_all_compulsory',
];
const kinds = ['topic', 'practical', 'skills', 'maths', 'assessment'];
const fail = (message) => {
  throw new TypeError(message);
};
function object(value, keys, label) {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !keys.includes(key))
  )
    fail(`Invalid ${label}.`);
  return value;
}
function text(value, max, label) {
  if (
    typeof value !== 'string' ||
    !value.trim() ||
    value.length > max ||
    [...value].some((character) => {
      const code = character.charCodeAt(0);
      return code < 32 && ![9, 10, 13].includes(code);
    })
  )
    fail(`Invalid ${label}.`);
  return value.trim();
}
function id(value, label) {
  if (typeof value !== 'string' || !ID.test(value)) fail(`Invalid ${label}.`);
  return value;
}
function ordinal(value, label) {
  if (!Number.isSafeInteger(value) || value < 1 || value > 10000) fail(`Invalid ${label}.`);
  return value;
}
function date(value) {
  if (
    typeof value !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    !Number.isFinite(Date.parse(value + 'T00:00:00Z')) ||
    new Date(value + 'T00:00:00Z').toISOString().slice(0, 10) !== value
  )
    fail('Invalid source date.');
  return value;
}
function sourceURL(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    fail('Invalid source URL.');
  }
  if (url.protocol !== 'https:' || url.username || url.password || value.length > 1500)
    fail('Invalid source URL.');
  return value;
}
function array(value, max, label) {
  if (!Array.isArray(value) || value.length > max) fail(`Invalid ${label}.`);
  return value;
}
function unique(values, label) {
  if (new Set(values).size !== values.length) fail(`Duplicate ${label}.`);
}
function scope(value) {
  object(value, ['tier', 'biologyOnly', 'asScope', 'separateScienceOnly'], 'lesson scope');
  if (
    !tiers.includes(value.tier) ||
    !biology.includes(value.biologyOnly) ||
    !asScopes.includes(value.asScope) ||
    (value.separateScienceOnly !== undefined &&
      (!biology.includes(value.separateScienceOnly) || value.biologyOnly !== 'not_applicable'))
  )
    fail('Invalid course applicability.');
  return { ...value };
}
/** Match validated scopes without broadening a course requirement. Exact scope
 * equality remains the default. The sole exception is the reviewed Pearson G7
 * negative-enlargement lesson: Higher-only teaching under the existing both-tier
 * topic. This boolean grants no publishing/review/assessment authority; every
 * other identity, requirement set and applicability difference fails closed. */
function lessonMatchesRequirementScope(courseId, lesson, requirementIds, required, lessonScope) {
  const keys = ['tier', 'biologyOnly', 'asScope', 'separateScienceOnly'];
  if (keys.every((key) => required.scope[key] === lessonScope[key])) return true;
  return (
    courseId === 'pearson-gcse-mathematics-1ma1' &&
    lesson.id === 'pearson-1ma1-geometry-negative-enlargement' &&
    requirementIds.length === 1 &&
    requirementIds[0] === 'topic-g7' &&
    required.id === 'topic-g7' &&
    required.scope.tier === 'both' &&
    lessonScope.tier === 'higher_only' &&
    lessonScope.biologyOnly === 'not_applicable' &&
    lessonScope.asScope === 'not_applicable' &&
    lessonScope.separateScienceOnly === undefined &&
    keys.slice(1).every((key) => required.scope[key] === lessonScope[key])
  );
}
function content(value) {
  object(value, ['paragraphs', 'workedExample', 'check'], 'lesson content');
  const paragraphs = array(value.paragraphs, 8, 'lesson paragraphs').map((value) =>
    text(value, 2500, 'lesson paragraph'),
  );
  if (paragraphs.length < 2) fail('A lesson needs explanatory paragraphs.');
  const example = object(value.workedExample, ['prompt', 'steps', 'answer'], 'worked example');
  const steps = array(example.steps, 8, 'worked steps').map((value) =>
    text(value, 1000, 'worked step'),
  );
  if (!steps.length) fail('A worked example needs reasoning.');
  const check = object(value.check, ['question', 'answer', 'rationale'], 'lesson check');
  return {
    paragraphs,
    workedExample: {
      prompt: text(example.prompt, 1500, 'example prompt'),
      steps,
      answer: text(example.answer, 1500, 'example answer'),
    },
    check: {
      question: text(check.question, 1500, 'check question'),
      answer: text(check.answer, 1500, 'check answer'),
      rationale: text(check.rationale, 2000, 'answer rationale'),
    },
  };
}
/** Validate operator-authored lesson artwork metadata. Only local course assets are allowed;
 * this is a provenance contract, not scientific review or authorization to publish a file. */
export function validateCourseVisual(value) {
  object(value, ['url', 'width', 'height', 'alt', 'credit', 'license', 'sha256'], 'lesson visual');
  if (
    typeof value.url !== 'string' ||
    !/^\/assets\/lesson-[a-z0-9.-]+\.(?:svg|webp|png)$/.test(value.url) ||
    !/^[a-f0-9]{64}$/.test(value.sha256 ?? '') ||
    !Number.isSafeInteger(value.width) ||
    !Number.isSafeInteger(value.height) ||
    value.width < 64 ||
    value.height < 64 ||
    value.width > 4096 ||
    value.height > 4096
  )
    fail('Invalid lesson visual asset.');
  return {
    url: value.url,
    width: value.width,
    height: value.height,
    alt: text(value.alt, 1000, 'lesson visual description'),
    credit: text(value.credit, 1000, 'lesson visual credit'),
    license: text(value.license, 500, 'lesson visual license'),
    sha256: value.sha256,
  };
}
/** Validate trusted manifests at ingestion and every persisted read. Unknown fields, duplicate
 * orders, missing requirements and prerequisite loops fail closed without partial installation.
 * This checks structure and applicability, not scientific accuracy or curriculum completeness. */
export function validateCourseManifest(input) {
  object(
    input,
    [
      'id',
      'title',
      'board',
      'qualificationCode',
      'qualification',
      'enrichment',
      'subject',
      'version',
      'publisherPackId',
      'source',
      'scopeNote',
      'modules',
      'requirements',
      'lessons',
    ],
    'course manifest',
  );
  const course = {
    id: id(input.id, 'course ID'),
    title: text(input.title, 160, 'course title'),
    board: text(input.board, 80, 'exam board'),
    qualificationCode: text(input.qualificationCode, 30, 'qualification code'),
    qualification: input.qualification,
    subject: text(input.subject, 80, 'subject'),
    version: ordinal(input.version, 'course version'),
    publisherPackId: id(input.publisherPackId, 'publisher pack'),
    scopeNote: text(input.scopeNote, 1500, 'course scope note'),
  };
  if (!['gcse', 'as', 'a_level', 'enrichment'].includes(course.qualification))
    fail('Invalid qualification.');
  if (course.qualification === 'enrichment') {
    const enrichment = object(
      input.enrichment,
      ['provider', 'certification'],
      'enrichment identity',
    );
    if (
      enrichment.provider !== 'myAudience' ||
      enrichment.certification !== 'none' ||
      course.board !== 'myAudience' ||
      course.qualificationCode !== 'independent'
    )
      fail('Enrichment must identify an independent myAudience pathway without certification.');
    course.enrichment = { provider: 'myAudience', certification: 'none' };
  } else if (Object.hasOwn(input, 'enrichment'))
    fail('School courses cannot carry enrichment metadata.');
  const source = object(input.source, ['url', 'checkedDate', 'issue'], 'course source');
  course.source = {
    url: sourceURL(source.url),
    checkedDate: date(source.checkedDate),
    issue: text(source.issue, 100, 'source version'),
  };
  course.modules = array(input.modules, 100, 'course modules').map((module) => {
    object(module, ['id', 'title', 'ordinal'], 'module');
    return {
      id: id(module.id, 'module ID'),
      title: text(module.title, 160, 'module title'),
      ordinal: ordinal(module.ordinal, 'module order'),
    };
  });
  if (!course.modules.length) fail('Course modules are required.');
  unique(
    course.modules.map((module) => module.id),
    'module IDs',
  );
  unique(
    course.modules.map((module) => module.ordinal),
    'module order',
  );
  const moduleIds = new Set(course.modules.map((module) => module.id));
  course.requirements = array(input.requirements, 2000, 'course requirements').map(
    (requirement) => {
      object(requirement, ['id', 'title', 'kind', 'moduleId', 'scope'], 'curriculum requirement');
      if (
        !kinds.includes(requirement.kind) ||
        (requirement.moduleId && !moduleIds.has(requirement.moduleId))
      )
        fail('Invalid requirement category or module.');
      return {
        id: id(requirement.id, 'requirement ID'),
        title: text(requirement.title, 160, 'requirement title'),
        kind: requirement.kind,
        moduleId: requirement.moduleId ?? null,
        scope: scope(requirement.scope),
      };
    },
  );
  if (!course.requirements.length) fail('Requirements are required.');
  unique(
    course.requirements.map((requirement) => requirement.id),
    'requirement IDs',
  );
  const requirements = new Map(
    course.requirements.map((requirement) => [requirement.id, requirement]),
  );
  course.lessons = array(input.lessons, 1000, 'course lessons').map((lesson) => {
    object(
      lesson,
      [
        'id',
        'title',
        'revision',
        'ordinal',
        'moduleId',
        'requirementIds',
        'specRefs',
        'prerequisiteLessonIds',
        'editorialReview',
        'content',
        'scope',
        'sourceURLs',
        'sourceCheckedDate',
        'visual',
      ],
      'lesson',
    );
    if (
      !moduleIds.has(lesson.moduleId) ||
      !['pending', 'reviewed'].includes(lesson.editorialReview)
    )
      fail('Invalid lesson module or review state.');
    const requirementIds = array(lesson.requirementIds, 30, 'lesson requirements').map((value) =>
      id(value, 'requirement reference'),
    );
    if (!requirementIds.length || requirementIds.some((value) => !requirements.has(value)))
      fail('Unknown lesson requirement.');
    unique(requirementIds, 'lesson requirement');
    const lessonScope = scope(lesson.scope);
    for (const reference of requirementIds) {
      const required = requirements.get(reference);
      if (!lessonMatchesRequirementScope(course.id, lesson, requirementIds, required, lessonScope))
        fail('Lesson scope must match its mapped requirements.');
    }
    const prerequisiteLessonIds = array(lesson.prerequisiteLessonIds, 20, 'prerequisites').map(
      (value) => id(value, 'prerequisite'),
    );
    unique(prerequisiteLessonIds, 'prerequisite');
    const sourceURLs = array(lesson.sourceURLs, 8, 'lesson sources').map(sourceURL);
    if (!sourceURLs.length) fail('Lesson sources are required.');
    return {
      id: id(lesson.id, 'lesson ID'),
      title: text(lesson.title, 160, 'lesson title'),
      revision: ordinal(lesson.revision, 'lesson revision'),
      ordinal: ordinal(lesson.ordinal, 'lesson order'),
      moduleId: lesson.moduleId,
      requirementIds,
      specRefs: array(lesson.specRefs, 30, 'specification references').map((value) =>
        text(value, 100, 'specification reference'),
      ),
      prerequisiteLessonIds,
      editorialReview: lesson.editorialReview,
      content: content(lesson.content),
      scope: lessonScope,
      sourceURLs,
      sourceCheckedDate: date(lesson.sourceCheckedDate),
      ...(lesson.visual === undefined ? {} : { visual: validateCourseVisual(lesson.visual) }),
    };
  });
  for (const lesson of course.lessons) courseLessonText(lesson);
  unique(
    course.lessons.map((lesson) => lesson.id),
    'lesson IDs',
  );
  unique(
    course.lessons.map((lesson) => lesson.ordinal),
    'lesson order',
  );
  for (const item of [...course.requirements, ...course.lessons]) {
    if (item.scope.tier === 'foundation_only' && course.qualification !== 'gcse')
      fail('Foundation-only applicability requires a GCSE course.');
    if (
      course.qualification === 'enrichment' &&
      Object.values(item.scope).some((value) => value !== 'not_applicable')
    )
      fail('Enrichment cannot claim school qualification applicability.');
  }
  const lessons = new Map(course.lessons.map((lesson) => [lesson.id, lesson]));
  for (const lesson of course.lessons)
    for (const prerequisite of lesson.prerequisiteLessonIds) {
      const before = lessons.get(prerequisite);
      if (!before || before.ordinal >= lesson.ordinal)
        fail('Prerequisites must exist earlier in the course.');
      if (lesson.editorialReview === 'reviewed' && before.editorialReview !== 'reviewed')
        fail('Reviewed lessons cannot depend on pending drafts.');
    }
  course.modules.sort((a, b) => a.ordinal - b.ordinal);
  course.lessons.sort((a, b) => a.ordinal - b.ordinal || a.id.localeCompare(b.id));
  return course;
}
/** Report available lesson mapping, not complete curricula. A topic lesson is not proof that
 * every specification bullet, skill or assessed practical has been taught or mastered. */
export function courseCoverage(course, availableLessonIds) {
  const available = course.lessons.filter(
    (lesson) => lesson.editorialReview === 'reviewed' && availableLessonIds.has(lesson.id),
  );
  const mapped = new Set(available.flatMap((lesson) => lesson.requirementIds));
  const categories = Object.fromEntries(
    kinds.map((kind) => [
      kind,
      {
        required: course.requirements.filter((requirement) => requirement.kind === kind).length,
        withPublishedLesson: course.requirements.filter(
          (requirement) => requirement.kind === kind && mapped.has(requirement.id),
        ).length,
      },
    ]),
  );
  return {
    authoredLessons: course.lessons.length,
    pendingReviewLessons: course.lessons.filter((lesson) => lesson.editorialReview === 'pending')
      .length,
    publishedAvailableLessons: available.length,
    categories,
    completeCurriculum: false,
    statement:
      course.qualification === 'enrichment'
        ? 'Published lessons map to selected learning objectives. Full pathway coverage has not been verified. This independent pathway offers no qualification or certificate.'
        : 'Published lessons map to selected requirements. Full specification and practical/skills coverage has not been verified.',
  };
}
/** Bounded, typed browsing contract. Cursors are opaque server tokens handled by the adapter. */
export function validateCourseQuery(input = {}) {
  object(input, ['moduleId', 'cursor', 'limit', 'preview'], 'course query');
  const query = {
    moduleId: input.moduleId ?? null,
    cursor: input.cursor ?? null,
    limit: input.limit === undefined ? 12 : Number(input.limit),
    preview: input.preview === 'true' || input.preview === true,
  };
  if (query.moduleId !== null) id(query.moduleId, 'module filter');
  if (input.preview !== undefined && !['true', 'false', true, false].includes(input.preview))
    fail('Invalid preview choice.');
  if (
    !Number.isInteger(query.limit) ||
    query.limit < 1 ||
    query.limit > 24 ||
    (typeof input.limit === 'string' && !/^(?:[1-9]|1\d|2[0-4])$/.test(input.limit))
  )
    fail('Page size must be 1–24.');
  if (
    query.cursor !== null &&
    (typeof query.cursor !== 'string' || !/^([a-f0-9]{48})\.(\d{1,5})$/.test(query.cursor))
  )
    fail('Invalid course cursor.');
  return query;
}

/** Compose the versioned lesson publication once. Publisher edits remain canonical post text;
 * callers can compare against this immutable exercise revision to avoid stale answer panels.
 * Reject the complete UTF-16 text above the maintained-course bound; never truncate teaching. */
export function courseLessonText(lesson) {
  const c = lesson.content;
  const text = [
    lesson.title,
    ...c.paragraphs,
    'Worked example: ' + c.workedExample.prompt,
    ...c.workedExample.steps,
    'Answer: ' + c.workedExample.answer,
    'Check your understanding: ' + c.check.question,
    'Answer: ' + c.check.answer,
    'Why: ' + c.check.rationale,
  ].join('\n\n');
  if (text.length > COURSE_POST_TEXT_LIMIT)
    throw new RangeError('Course lesson exceeds its complete publication text budget.');
  return text;
}

/** Describe a validated course/header without presenting an independent pathway as an exam
 * qualification. School identities retain their existing wording and immutable payloads. */
export function courseGuideLabel(course) {
  return course.qualification === 'enrichment'
    ? `Independent myAudience learning pathway · no qualification or certificate · version ${course.version}`
    : `${course.board} ${course.qualificationCode} · independent myAudience guide · version ${course.version}`;
}

export const EDITION = 'biology-lesson-notes-1';
const supplementSHA = (value) => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
function supplementClosed(value, keys, label) {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.keys(value).some((k) => !keys.includes(k))
  )
    throw new TypeError('Invalid ' + label + '.');
}
/** Proposed addition to canonical domain26; scientific acceptance is external. Exact source
 * identity, immutable lessons, finite text and lossless canonical23 segments are required. */
export function validateSupplementEdition(input, catalog, fingerprint) {
  if (typeof fingerprint !== 'function')
    throw new TypeError('Trusted fingerprint function required.');
  supplementClosed(input, ['edition', 'approved', 'entries'], 'lesson notes edition');
  if (
    input.edition !== EDITION ||
    typeof input.approved !== 'boolean' ||
    !Array.isArray(input.entries) ||
    input.entries.length !== 50
  )
    throw new TypeError('Invalid lesson notes edition.');
  const courses = new Map(
    catalog.map((c) => {
      const v = validateCourseManifest(c);
      return [v.id, v];
    }),
  );
  const seen = new Set();
  for (const row of input.entries) {
    supplementClosed(
      row,
      [
        'courseId',
        'lessonId',
        'lessonRevision',
        'publisherPackId',
        'baseLessonSHA256',
        'publicationSHA256',
        'postBaselineSHA256',
        'selectedVisualSHA256',
        'paragraphs',
        'sourceURLs',
        'checkedDate',
        'draftSHA256',
      ],
      'lesson notes entry',
    );
    const course = courses.get(row.courseId),
      lesson = course?.lessons.find(
        (l) => l.id === row.lessonId && l.revision === row.lessonRevision,
      ),
      key = JSON.stringify([row.courseId, row.lessonId, row.lessonRevision]);
    if (
      !lesson ||
      lesson.editorialReview !== 'reviewed' ||
      course.publisherPackId !== row.publisherPackId ||
      fingerprint(lesson) !== row.baseLessonSHA256 ||
      seen.has(key) ||
      ![
        row.baseLessonSHA256,
        row.publicationSHA256,
        row.postBaselineSHA256,
        row.selectedVisualSHA256,
        row.draftSHA256,
      ].every(supplementSHA)
    )
      throw new TypeError('Lesson notes identity changed.');
    seen.add(key);
    if (
      !Array.isArray(row.paragraphs) ||
      row.paragraphs.length < 2 ||
      row.paragraphs.length > 8 ||
      row.paragraphs.some(
        (p) =>
          typeof p !== 'string' ||
          !p.trim() ||
          p.length > 2500 ||
          [...p].some((char) => {
            const code = char.charCodeAt(0);
            return code < 32 && ![9, 10, 13].includes(code);
          }),
      )
    )
      throw new TypeError('Invalid lesson notes paragraphs.');
    const text = row.paragraphs.join('\n\n');
    if (
      text.length > COURSE_POST_TEXT_LIMIT ||
      splitPostText(text, { maintainedCourse: true }).join('') !== text
    )
      throw new TypeError('Invalid lesson notes text budget.');
    if (
      !Array.isArray(row.sourceURLs) ||
      row.sourceURLs.length < lesson.sourceURLs.length ||
      row.sourceURLs.length > 12 ||
      new Set(row.sourceURLs).size !== row.sourceURLs.length ||
      JSON.stringify(row.sourceURLs.slice(0, lesson.sourceURLs.length)) !==
        JSON.stringify(lesson.sourceURLs) ||
      row.sourceURLs.some((source) => {
        if (typeof source !== 'string' || source.length > 1000) return true;
        try {
          const parsed = new URL(source);
          return parsed.protocol !== 'https:' || Boolean(parsed.username || parsed.password);
        } catch {
          return true;
        }
      }) ||
      typeof row.checkedDate !== 'string' ||
      !/^\d{4}-\d{2}-\d{2}$/.test(row.checkedDate) ||
      !Number.isFinite(Date.parse(row.checkedDate + 'T00:00:00Z')) ||
      new Date(row.checkedDate + 'T00:00:00Z').toISOString().slice(0, 10) !== row.checkedDate
    )
      throw new TypeError('Invalid lesson notes provenance.');
  }
  return structuredClone(input);
}
/** Notes have their own identity/fulltext, never alter the post fullText or member comments.
 * This reuses canonical grapheme/whitespace continuation policy, not an alternate splitter. */
export function noteDTO(row, value) {
  const text = value.paragraphs.join('\n\n'),
    segments = splitPostText(text, { maintainedCourse: true });
  return {
    id: row.note_id,
    authorId: row.publisher_id,
    label: 'Lesson notes',
    fullText: text,
    sourceURLs: value.sourceURLs,
    sourceCheckedDate: value.checkedDate,
    parts: segments.map((text, index) => ({
      id: `${row.note_id}-part-${index + 1}`,
      index: index + 1,
      total: segments.length,
      text,
    })),
  };
}
/** Compile only immutable reviewed root data at startup, not a viewer/ACL cache. Per-request
 * projection compares persisted note bytes to this authority rather than trusting DB flags. */
export function compileSupplementDefinition(input, catalog, fingerprint) {
  const edition = validateSupplementEdition(input, catalog, fingerprint);
  return {
    edition: edition.edition,
    approved: edition.approved,
    payloadHash: fingerprint(edition.entries),
    entryHashes: new Map(
      edition.entries.map((row) => [
        JSON.stringify([row.courseId, row.lessonId, row.lessonRevision]),
        fingerprint(row),
      ]),
    ),
  };
}

/** Public discovery remains the immutable main course reading only. Supplemental notes
 * require member authorization and are not indexed or exported as member engagement. */
export const COURSE_SUPPLEMENT_PUBLIC_DISCOVERY = false;
